// Iteration 140 -- "Message coach" launcher for parents (My Children).
// Picks one of the child's teams and opens a thread with that team's coaches.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ChildTeamPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { X, Send } from "lucide-react";

interface Props {
  playerId: string;
  playerName: string;
  onClose: () => void;
}

export function MessageCoachDialog({ playerId, playerName, onClose }: Props) {
  const navigate = useNavigate();
  const [teams, setTeams] = useState<ChildTeamPublic[] | null>(null);
  const [teamId, setTeamId] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .getChildTeams(playerId)
      .then((res) => {
        setTeams(res.teams);
        if (res.teams.length === 1) setTeamId(res.teams[0].id);
      })
      .catch(() => setTeams([]));
  }, [playerId]);

  async function send() {
    if (!teamId || !body.trim() || sending) return;
    setSending(true);
    setError(null);
    try {
      const { id } = await api.startConversation({ player_id: playerId, team_id: teamId, body: body.trim() });
      navigate(`/inbox?c=${id}`);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to send");
      setSending(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/40 p-4 sm:items-center" role="dialog" aria-modal="true">
      <div className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl border border-border bg-background p-5 shadow-xl" data-dialog="message-coach">
        <div className="mb-3 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold">Message {playerName}'s coach</h2>
            <p className="text-sm text-muted-foreground">Your message goes to the team's coaches.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-md p-1 text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        {teams === null ? (
          <p className="text-sm text-muted-foreground">Loading teams...</p>
        ) : teams.length === 0 ? (
          <p className="text-sm text-muted-foreground">This child isn't on any team yet, so there's no coach to message.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {teams.length > 1 && (
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium">Team</span>
                <select
                  value={teamId}
                  onChange={(e) => setTeamId(e.target.value)}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
                >
                  <option value="">Select a team...</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </label>
            )}
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              placeholder="Type your message..."
              className="resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
            />
            {error && <p className="text-sm text-destructive" data-error>{error}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
              <Button size="sm" onClick={send} disabled={sending || !teamId || !body.trim()} data-action="send-message-coach">
                <Send className="mr-1 h-4 w-4" /> Send
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
